import { useLocation } from 'react-router-dom';
import type { AuthChallenge } from '../api/types';

/** A sign-up waiting for its code: who, and where it was sent (masked). */
export interface PendingConfirmation {
  email: string;
  destination: string | null;
  medium: 'EMAIL' | 'SMS' | null;
}

/** What the auth pages hand each other through router state (never the URL). */
export interface AuthRouteState {
  /** Path to return to after sign-in. */
  from?: string;
  /** Pending login challenge for /sign-in/challenge. */
  challenge?: AuthChallenge;
  /** One-line notice to show at the top of the page. */
  notice?: string;
  /** Prefilled email for /reset-password and /sign-in. */
  email?: string;
  /** Sign-up awaiting its confirmation code, for /sign-up/confirm. */
  confirm?: PendingConfirmation;
}

export function useAuthRouteState(): AuthRouteState {
  const { state } = useLocation();
  return state && typeof state === 'object' ? (state as AuthRouteState) : {};
}

export const E164 = /^\+[1-9]\d{6,14}$/;
export const MIN_PASSWORD_LENGTH = 8;
export const NAME_MAX_LENGTH = 64;

/** Mirrors the API's rule for first / last names: required, at most 64 characters once trimmed. */
export function nameProblem(value: string): string | null {
  const clean = value.trim();
  if (!clean) return 'Required.';
  if (clean.length > NAME_MAX_LENGTH) return `At most ${NAME_MAX_LENGTH} characters.`;
  return null;
}

/** Client-side pre-check that mirrors the API's password rule (the provider enforces the policy). */
export function passwordProblem(password: string, confirm?: string): string | null {
  if (password.length < MIN_PASSWORD_LENGTH)
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  if (confirm !== undefined && confirm !== password) return 'The passwords do not match.';
  return null;
}
